import { useCallback, useEffect, useMemo, useState } from "react";
import type { PortalAccountType, SigningDocumentType } from "@aam/shared";
import { accountTypeRequiresContracting } from "@aam/shared";
import { getSessionToken, useSession } from "./useSession.js";
import {
  isTrainingComplete,
  readDesktopAppConfirmed,
  readWatchedTrainingSteps,
} from "../components/portal/setupStorage.js";
import type { SetupProgress } from "../components/portal/setupSteps.js";
import { EMPTY_SETUP_PROGRESS } from "../components/portal/setupSteps.js";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:4000";

export interface DialerEnrollment {
  product: "DIALER_INTELELEAD" | "INSURANCE_TOOLKIT";
  status: string;
  failureMessage: string | null;
  hasPaidOrder: boolean;
  softPhone: { callDeliveryNumber: string; username: string } | null;
}

export interface SigningDocumentEntry {
  documentType: SigningDocumentType;
  status: "NOT_STARTED" | "SIGNED";
}

export interface SetupProgressState {
  progress: SetupProgress;
  loading: boolean;
  error: boolean;
  agentId: string | null;
  /** STANDARD (contracted) or RING_IQ (dialer-only). */
  accountType: PortalAccountType;
  contracts: { total: number; active: number; pending: number };
  dialer: DialerEnrollment | null;
  signingDocuments: SigningDocumentEntry[];
  /** Re-reads the API-backed halves of the progress (steps 1 and 2). */
  refresh: () => void;
  /** Re-reads the locally stored halves (steps 3 and 4) after a page updates them. */
  refreshLocal: () => void;
}

/**
 * Single source of truth for how far an agent is through setup. Steps 1 and 2
 * are read from the API; steps 3 and 4 are local (see setupStorage) because the
 * backend has no way to observe a watched video or an installed desktop app.
 */
export function useSetupProgress(): SetupProgressState {
  const token = getSessionToken();
  const { agent } = useSession();
  const agentId = agent?.id ?? null;
  const accountType: PortalAccountType = agent?.accountType ?? "STANDARD";
  const [contracts, setContracts] = useState({ total: 0, active: 0, pending: 0 });
  const [dialer, setDialer] = useState<DialerEnrollment | null>(null);
  const [signingDocuments, setSigningDocuments] = useState<SigningDocumentEntry[]>([]);
  const [surelcGate, setSurelcGate] = useState<{ required: boolean; proceeded: boolean }>({
    required: false,
    proceeded: false,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [localKey, setLocalKey] = useState(0);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(false);
    const headers = { Authorization: `Bearer ${token}` };

    const summary = fetch(`${API_BASE}/api/agents/me`, { headers })
      .then(async (response) => {
        const json = await response.json();
        if (!response.ok || !json.success) throw new Error(json.error || "Unable to load setup progress");
        return json.data;
      })
      .then((data) => {
        if (cancelled) return;
        const rows: { status?: string }[] = Array.isArray(data.contracts) ? data.contracts : [];
        const total = typeof data.summary?.contractCount === "number" ? data.summary.contractCount : rows.length;
        setContracts({
          total,
          active: rows.filter((contract) => contract.status === "ACTIVE").length,
          pending: rows.filter((contract) => contract.status === "PENDING").length,
        });
      });

    const enrollments = fetch(`${API_BASE}/api/services/enrollments`, { headers })
      .then(async (response) => {
        const json = await response.json();
        if (!response.ok || !json.success) throw new Error(json.error || "Unable to load service status");
        return (Array.isArray(json.data) ? json.data : []) as DialerEnrollment[];
      })
      .then((rows) => {
        if (cancelled) return;
        setDialer(rows.find((entry) => entry.product === "DIALER_INTELELEAD") ?? null);
      });

    // Signing status is fetched alongside but kept non-critical: a failure here
    // leaves steps 5 and 6 reading as unsigned rather than erroring the page.
    void fetch(`${API_BASE}/api/signing-documents`, { headers })
      .then(async (response) => {
        const json = await response.json();
        if (!response.ok || !json.success) throw new Error(json.error || "Unable to load document status");
        return (Array.isArray(json.data) ? json.data : []) as SigningDocumentEntry[];
      })
      .then((entries) => {
        if (!cancelled) setSigningDocuments(entries);
      })
      .catch(() => {
        // Non-critical.
      });

    // SureLC-path agents unlock setup with Proceed. A failure here falls back to
    // the contract-count rule, which is what the flag-off behavior is anyway.
    const surelc = fetch(`${API_BASE}/api/agents/me/surelc-onboarding`, { headers })
      .then(async (response) => {
        const json = await response.json();
        if (!response.ok || !json.success) throw new Error(json.error || "Unable to load SureLC status");
        return json.data as { proceedRequired?: boolean; proceededAt?: string | null };
      })
      .then((data) => {
        if (!cancelled) {
          setSurelcGate({ required: Boolean(data.proceedRequired), proceeded: Boolean(data.proceededAt) });
        }
      })
      .catch(() => {
        // Non-critical.
      });

    void Promise.all([summary, enrollments, surelc])
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, reloadKey]);

  const progress = useMemo<SetupProgress>(() => {
    if (!agentId) return EMPTY_SETUP_PROGRESS;
    // localKey is only here to re-read localStorage after a step page writes to
    // it; the values themselves come straight out of storage.
    void localKey;
    const watched = readWatchedTrainingSteps(agentId);
    const training = isTrainingComplete(watched);
    const signed = (documentType: SigningDocumentType) =>
      signingDocuments.some((entry) => entry.documentType === documentType && entry.status === "SIGNED");
    return {
      // RingIQ agents never contract, so step 1 is satisfied by definition and
      // their setup opens on the dialer fee.
      contracting:
        !accountTypeRequiresContracting(accountType) ||
        (surelcGate.required ? surelcGate.proceeded : contracts.total > 0),
      dialer: Boolean(dialer?.hasPaidOrder),
      training,
      // Calibration can only count as done once the videos are, so a stale flag
      // from a reset can never jump the agent to the end.
      desktopApp: training && readDesktopAppConfirmed(agentId),
      achForm: signed("ACH_FORM"),
      ccAuthForm: signed("CC_AUTH_FORM"),
    };
  }, [accountType, agentId, contracts.total, dialer, localKey, signingDocuments, surelcGate]);

  const refresh = useCallback(() => setReloadKey((key) => key + 1), []);
  const refreshLocal = useCallback(() => setLocalKey((key) => key + 1), []);

  return {
    progress,
    loading,
    error,
    agentId,
    accountType,
    contracts,
    dialer,
    signingDocuments,
    refresh,
    refreshLocal,
  };
}
